import { useHistory } from "react-router";

import { ApiResponse, SearchRights } from "@/api/elasticSearch/vmsSearchApi";
import { PageType } from "@/utilities/types/common";
import { ArchiveCabinetSearchData, LiveUnitSearchData, RegionSearchData, ResultTypes, SearchResult, SearchSourceType, SelectedClient, SelectedRegion } from "@/utilities/types/search";

import ArchiveCabinetResult from "./Results/ArchiveCabinetResult";
import ClientResult from "./Results/ClientResult";
import LiveUnitResult from "./Results/LiveUnitResult";
import RegionResult from "./Results/RegionResult";
import { canRequestVideo, getStreamCamera, hasCameraRights, onClickArchiveCabinet } from "./VmsSearch.Utils";


interface Props {
    index: number,
    result: SearchResult<ResultTypes>,
    searchRightsData: ApiResponse<SearchRights> | undefined,
    navigateToStream: (result: SearchResult<LiveUnitSearchData>) => void,
    pageType: PageType,
    navigateToStreamAndClose: (result: SearchResult<LiveUnitSearchData>) => void,
    closeSearchBody: () => void,
    onSelectLiveUnit: (searchResult: SearchResult<LiveUnitSearchData>) => void,
    onSelectArchiveCabinet: (cabinetId: number) => void,
    onClientSelected: (client: SelectedClient) => void,
    onRegionSelected: (region: SelectedRegion) => void,

}

export const VmsSearchResults: React.FC<Props> = (
    {
        onRegionSelected,
        onClientSelected,
        onSelectArchiveCabinet,
        onSelectLiveUnit,
        index,
        result,
        searchRightsData,
        closeSearchBody,
        pageType ,
        navigateToStreamAndClose,
    },
) => {
    let onSelect;
    const history = useHistory();

    switch (result.source.type) {
        case SearchSourceType.LiveUnit:
            // Using the getStreamCamera function to determine if the user has permissions to stream
            // a camera. Not using it for determining the stream camera because there are more
            // conditions than are currently included in the elastic search data.
            const hasPermissionToStream = searchRightsData && getStreamCamera(searchRightsData.data, result.source.client_id, result.source.primary_camera || null, result.source.cameras) != null;
            const historyUnitId = canRequestVideo({ client_id: result.source.client_id, camera_ids: result.source.cameras, searchRightsData, hasCameraRights }) ? result.source.id : null;
            const onStreamClick = hasPermissionToStream ? navigateToStreamAndClose : null;
            const onHistoryClick = () => {
                history.push(`/live-unit/${historyUnitId}/history`);
                closeSearchBody();
            };

            switch (pageType) {
                case PageType.Stream:
                    onSelect = onStreamClick;
                    break;
                case PageType.History:
                    onSelect = onHistoryClick;
                    break;

                default:
                    onSelect = onSelectLiveUnit;
                    break;
            }

            return (<LiveUnitResult
                key={index}
                result={result as SearchResult<LiveUnitSearchData>}
                historyUnitId={historyUnitId}
                onSelect={onSelect}
                onStreamClick={onStreamClick}
                onHistoryClick={onHistoryClick}
            />);
        case SearchSourceType.ArchiveCabinet:
            // Check if the user has perms to see historic media for an AC.
            const acHistoryUnitId = canRequestVideo({ client_id: result.source.client_id, camera_ids:[result.source.id], searchRightsData, hasCameraRights }) ? result.source.id : null;

            switch (pageType) {
                // If we search for an AC while being on the streaming or history page, we need to be redirected appropriately.
                case PageType.Stream:
                case PageType.History:
                    onSelect = () => {
                        onClickArchiveCabinet(history, acHistoryUnitId, closeSearchBody);
                    };
                    break;

                default:
                    onSelect = onSelectArchiveCabinet;
                    break;
            }

            return (<ArchiveCabinetResult
                key={index}
                result={result as SearchResult<ArchiveCabinetSearchData>}
                onSelect={onSelect}
            />);
        case SearchSourceType.Client:
            return <ClientResult key={index} index={index} id={result.source.id} name={result.source.name} onSelect={onClientSelected} />;
        case SearchSourceType.Region:
            return <RegionResult key={index} index={index} result={result as SearchResult<RegionSearchData>} isMultiClient={searchRightsData ? searchRightsData.data.is_multi_client : false} onSelect={onRegionSelected} />;

    }
};
